import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useMutation } from 'hooks/sympl-mutation';
import { Controller, useForm } from 'react-hook-form';

import ApiDropdown from 'components/dropdown/api-dropdown/ApiDropdown';
import Input from 'components/form/input/Input';
import SettingsInput from 'components/form/input/SettingsInput';
import AddressInput from 'components/form/AddressInput';
import VatInput from 'components/form/VatInput';
import SaveButton from 'components/save-button/SaveButton';
import { CustomerAddress } from 'types/geolocationTypes';
import { MAIL_REGEX, VAT_NUMBER_REGEX } from 'utils/regexHelpers';
import Container from 'components/container/Container';
import ContainerHeader from 'components/container/container-header/ContainerHeader';
import ContainerAction from 'components/container/container-action/ContainerAction';
import { UPDATE_CUSTOMER } from 'graphql/customers/mutations';
import { Customer } from 'types/customer/types';
import { Footnote, Subhead, Title2 } from 'components/typography/Typography';
import { addressValidationHandler } from 'utils/addressHelpers';
import useUnsavedFields from 'hooks/unsaved';
import useNavigationContext from 'hooks/context/nav-context';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import tw from 'twin.macro';
import ToggleButton from 'components/toggle-button/ToggleButton';
import InformationToolTip from 'components/tooltip/InformationToolTip';
import PermissionWrapper from 'components/permissions/permissionWrapper/PermissionWrapper';
import { PERMISSIONS } from 'context/PermissionContext';
import usePermissionContext from 'hooks/context/permission-context';
import Button from 'components/button/Button';
import { Modal } from 'components/page/app-page/Modal';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import { useKeptVersion } from 'api/keptVersion';
import {
  GeneralSettingsWork,
  INVOICING_NOT_COMPLETE,
  NO_FINANCE_RIGHTS,
  customerPath,
  generalSettingsBody,
  missingForGeneralSettings,
  monthsOf,
  saveGeneralSettings,
  shortenQuestion,
  twoFactorQuestion,
} from 'api/generalSettingsAutoSave';

type SettingsInputs = {
  name: string;
  invoicing_name: string;
  invoicing_email: string;
  invoice_address: CustomerAddress;
  vat: string | null;
  legal_form: string;

  // the api is returning the number as string. a bit confused on this part , so I added number , string and null
  default_po_number: string | null;
  gdpr_deletion_period: number;

  // 2fa
  two_factor_enabled: boolean;
};

type FieldValue = string | boolean | CustomerAddress;

type UpdateCustomerFormKey = keyof Omit<Customer, 'subscription'>;

interface UpdateCustomerPayload {
  input: Customer;
  customerId: number;
}

const GeneralSettings = ({
  saveLabel,
  onSave,
  minimal = false,
}: {
  saveLabel?: string;
  onSave?: () => void;
  minimal?: boolean;
}) => {
  const { customer, activeCustomer, refetchCustomer } = useNavigationContext();
  const { addToast } = useToastNotifications();

  const formMethods = useForm<SettingsInputs>({
    defaultValues: {
      name: '',
      invoicing_name: '',
      invoicing_email: '',
      invoice_address: undefined,
      vat: '',
      legal_form: '',
      default_po_number: '',
      gdpr_deletion_period: undefined,
      two_factor_enabled: customer?.two_factor_enabled || false,
    },
  });

  const {
    control,
    errors: formErrors,
    handleSubmit,
    reset,
    setValue,
  } = formMethods;

  const [shouldSaveChanges, resetUnsavedFlag] = useUnsavedFields(formMethods);

  /*
   * Auto-save (scenes 15 and 21; Steven's answers of 5 Oct 2026). On the
   * settings screen the form stores itself. Inside the first publication of
   * a campaign (`minimal`) it does not: there "Continue" stores, as before.
   */
  const { userHasPermission } = usePermissionContext();
  const mayChange = userHasPermission(PERMISSIONS.MANAGE_FINANCES);
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();
  /** The customer whose settings were taken over into the form. */
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  const takenFor = useRef<number | null>(null);

  /*
   * The screen fills itself from the app's one copy of the customer, read
   * when the app was opened. On the settings screen that copy is read
   * afresh first: taken as it was, the form showed values from before this
   * person's own earlier stores (or a colleague's), and the next store sent
   * them all back (review of the slice).
   */
  const [fresh, setFresh] = useState(minimal);
  useEffect(() => {
    if (minimal) return;
    Promise.resolve(refetchCustomer())
      .catch(() => undefined)
      .finally(() => setFresh(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!customer || !fresh) return;
    // On the settings screen: ONCE per customer. The customer is handed
    // over again whenever anything in the app reads it afresh, and taking
    // it over again would put the fields back under the person's hands.
    if (!minimal && takenFor.current === customer.id) return;
    takenFor.current = customer.id;
    reset(customer);
    setLoadedFor(customer.id);
  }, [customer, reset, minimal, fresh]);

  const own = !!customer && loadedFor === customer.id;
  const storesItself = !minimal && own && mayChange;
  /** Greyed out: the screen is there to read, not to change (D18). */
  const locked = !minimal && !mayChange;
  /*
   * Scene 28: the invoicing step inside a first publication, for someone
   * without finance rights. The fields are greyed out, a line says who can
   * fill them in, and "Continue" stays a button that is greyed out.
   */
  const lockedStep = minimal && !mayChange;

  const watched = formMethods.watch();
  const print = storesItself
    ? JSON.stringify({
        name: watched.name ?? '',
        invoicing_name: watched.invoicing_name ?? '',
        invoicing_email: watched.invoicing_email ?? '',
        invoice_address: watched.invoice_address ?? null,
        // "Nothing" and an empty box are the same: tabbing through the VAT
        // box turned one into the other and counted as a change.
        vat: watched.vat || null,
        legal_form: watched.legal_form ?? '',
        default_po_number: watched.default_po_number || null,
        // As a number when it is one: "36" and 36 are the same period.
        gdpr_deletion_period:
          monthsOf(watched.gdpr_deletion_period) ??
          watched.gdpr_deletion_period ??
          null,
        two_factor_enabled: !!watched.two_factor_enabled,
      })
    : null;
  const work = useMemo<GeneralSettingsWork | undefined>(
    () => (print ? (JSON.parse(print) as GeneralSettingsWork) : undefined),
    [print]
  );

  const missing = work ? missingForGeneralSettings(work) : [];
  const incomplete = missing.length > 0;
  /*
   * What the server holds of this screen, as far as the screen knows. While
   * a field is empty or wrong, and while a shorter deletion period has not
   * been confirmed, the saver is handed THIS: nothing goes out (D34, D35).
   */
  const storedWork = useRef<{ customer: number; work: GeneralSettingsWork }>();
  if (storesItself && work && customer) {
    if (storedWork.current?.customer !== customer.id) {
      storedWork.current = { customer: customer.id, work };
    }
  } else if (!own) {
    storedWork.current = undefined;
  }
  const storedPrint = storedWork.current
    ? JSON.stringify(storedWork.current.work)
    : null;

  /** A shorter deletion period deletes candidates' data for good: it asks first. */
  const [shorterAgreed, setShorterAgreed] = useState<number | null>(null);
  const shorterAgreedNow = useRef<number | null>(null);
  shorterAgreedNow.current = shorterAgreed;
  const monthsNow = monthsOf(work?.gdpr_deletion_period);
  const monthsStored = monthsOf(storedWork.current?.work.gdpr_deletion_period);
  const shorterWaits =
    !incomplete &&
    monthsNow !== null &&
    monthsStored !== null &&
    monthsNow < monthsStored &&
    shorterAgreed !== monthsNow;
  // Asked once the number has stood still for a moment, not at every digit.
  const [askShorter, setAskShorter] = useState(false);
  useEffect(() => {
    setAskShorter(false);
    if (!shorterWaits) return undefined;
    const soon = window.setTimeout(() => setAskShorter(true), 1200);
    return () => window.clearTimeout(soon);
  }, [shorterWaits, monthsNow]);

  const storable = useMemo<GeneralSettingsWork | undefined>(
    () => {
      if (!print) return undefined;
      if (incomplete && storedPrint) return JSON.parse(storedPrint);
      const now = JSON.parse(print) as GeneralSettingsWork;
      // A shorter period that waits for its answer holds back ONLY the
      // period: the other fields are stored as usual, so nothing else is
      // lost if the person leaves while the question is up.
      return shorterWaits && storedWork.current
        ? {
            ...now,
            gdpr_deletion_period: storedWork.current.work.gdpr_deletion_period,
          }
        : now;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [print, incomplete, shorterWaits, storedPrint]
  );
  const heldBack = storesItself && incomplete && print !== storedPrint;
  const heldBackRef = useRef(heldBack);
  heldBackRef.current = heldBack;

  const version = useKeptVersion(
    'customer',
    storesItself ? customer?.id : null
  );

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<GeneralSettingsWork>({
    value: storable,
    enabled: storesItself && !!storable,
    identity: customer ? `general-settings:${customer.id}` : null,
    save: async (value, signal) => {
      if (!customer) throw new Error('No customer is open.');
      const id = customer.id;
      const agreed = shorterAgreedNow.current;
      await saveGeneralSettings(
        id,
        {
          ...generalSettingsBody(
            customer as unknown as Record<string, unknown>,
            value
          ),
          // The server takes a shorter period only with this: the number
          // the person said "Shorten" to.
          ...(agreed !== null && agreed === monthsOf(value.gdpr_deletion_period)
            ? { shorter_period_confirmed: agreed }
            : {}),
        },
        signal,
        {
          basedOn: version.basedOn(id),
          stored: (count) => version.stored(id, count),
        }
      );
      if (storedWork.current?.customer === id) {
        storedWork.current = { customer: id, work: value };
      }
      // An agreement is for one store: the same shorter number later on
      // asks again.
      if (agreed !== null && agreed === monthsOf(value.gdpr_deletion_period)) {
        setShorterAgreed(null);
      }
      // The app's own copy of the customer follows what is stored.
      refetchCustomer();
    },
    flushOnHide: (value) => {
      if (customer)
        sendKeepaliveSave(
          customerPath(customer.id),
          generalSettingsBody(
            customer as unknown as Record<string, unknown>,
            value
          ),
          'PUT',
          version.basedOn(customer.id)
        );
    },
  });

  const workNow = useRef(work);
  workNow.current = work;
  const { canUndo, canRedo, undo, redo } = useUndoRedo<GeneralSettingsWork>({
    value: work,
    enabled: storesItself,
    identity: customer ? `general-settings:${customer.id}` : null,
    same: (a, b) => JSON.stringify(a) === JSON.stringify(b),
    apply: (earlier) => {
      // A step back to a SHORTER period asks like any other shorter period
      // (and the server takes none without the answer): Undo after "Cancel"
      // used to store the cancelled number (review of the slice).
      // The two-factor switch asks before it changes, also when Undo or
      // Redo is what would change it (Steven, 5 Oct 2026): the step is
      // taken for everything else, and the switch waits for the answer.
      const switchNow = !!workNow.current?.two_factor_enabled;
      const flips = !!earlier.two_factor_enabled !== switchNow;
      reset({
        ...(customer as Customer),
        ...earlier,
        two_factor_enabled: switchNow,
      } as never);
      if (flips) setAskTwoFactor(!!earlier.two_factor_enabled);
    },
  });

  useBeforeUnloadWarning(heldBack);

  // What the sidebar and the settings tabs ask before taking the person away.
  useEffect(() => {
    if (!storesItself) return undefined;
    setPending(
      (hasPending && hasFailed) || heldBack,
      heldBack ? missing[0] : undefined
    );
    registerAbandon(abandon);
    registerSaveNow(
      () => (heldBackRef.current ? Promise.resolve(false) : saveNow()),
      () => !heldBackRef.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storesItself,
    hasPending,
    hasFailed,
    heldBack,
    missing[0],
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  /** The two-factor switch asks before it changes (both ways). */
  const [askTwoFactor, setAskTwoFactor] = useState<boolean | null>(null);

  const [
    updateCustomer,
    { loading: updateCustomerLoading, error: updateCustomerError },
  ] = useMutation<undefined, UpdateCustomerPayload>(UPDATE_CUSTOMER);

  const settingSaveHandler = async (data: Customer) => {
    // Validate save request
    if (!activeCustomer) return;

    // Update the customer props
    const updatedCustomer: any = {
      ...customer,
      ...data,
      office_address: data.invoice_address, // TODO: remove this temporary fix
    };

    // Update the customer settings
    await updateCustomer({
      variables: {
        input: updatedCustomer,
        customerId: customer?.id ?? 0,
      },
    });

    addToast({
      type: ToastTypes.SUCCESS,
      description: 'Settings saved successfully',
    });

    // workaround for visual bug
    if (updatedCustomer.two_factor_enabled !== customer?.two_factor_enabled) {
      setValue('two_factor_enabled', updatedCustomer.two_factor_enabled);
    }

    resetUnsavedFlag();
    refetchCustomer();

    onSave?.();
  };

  const hasFormErrors = useMemo(
    () => Object.keys(formErrors).length > 0,
    [formErrors]
  );

  const updateField = (
    field: UpdateCustomerFormKey,
    value: FieldValue,
    onChange: (value: FieldValue) => void
  ) => {
    setValue(field, value);
    onChange(value);
  };

  const vatBlurHandler = (
    { target: { value } }: React.FocusEvent<HTMLInputElement>,
    onChange: (value: FieldValue) => void
  ) => {
    updateField('vat', value, onChange);
  };

  return (
    <div>
      <Container>
        <form
          onSubmit={
            minimal
              ? handleSubmit(settingSaveHandler)
              : (event) => event.preventDefault()
          }
        >
          {/*
            At the top and kept in view while the screen scrolls: at the bottom
            "Not saved: company name is required" could not be seen from the
            field it is about (Steven's hand test of 7 Oct 2026).
          */}
          {storesItself && (
            <div
              tw="sticky top-0 z-10 flex items-center justify-end gap-2 bg-white py-2"
              data-testid="general-settings-save-line"
            >
              <UndoRedo
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={undo}
                onRedo={redo}
              />
              <SaveStatus
                state={saveState}
                keptAt={keptAt}
                switching={switching}
                notSavedBecause={
                  heldBack ? `Not saved: ${missing[0]}` : undefined
                }
              />
            </div>
          )}
          {/* Without finance rights the fields are there to read (D18). */}
          <fieldset
            // Also until this customer's own settings are in the form.
            disabled={locked || lockedStep || (!minimal && !own)}
            css={[
              tw`border-0 p-0 m-0 min-w-0`,
              (locked || lockedStep) && tw`opacity-60 pointer-events-none`,
            ]}
            data-testid="general-settings-fields"
          >
            {!minimal && (
              <>
                <ContainerHeader>
                  <Title2 mb={0}>Company Details</Title2>
                  <Subhead isLight>
                    Practical information we need from you.
                  </Subhead>
                </ContainerHeader>
                <hr tw="mt-4" />
              </>
            )}
            <SettingsInput
              label="Name"
              errorMessage="This field is required"
              hasError={formErrors['name'] !== undefined}
            >
              <Controller
                id="name"
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={value}
                        ariaInvalid={formErrors['name'] !== undefined}
                        onChange={(e) =>
                          updateField('name', e.target.value, onChange)
                        }
                      />
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="Invoicing Name"
              errorMessage="This field is required"
              hasError={formErrors['invoicing_name'] !== undefined}
            >
              <Controller
                id="invoicing_name"
                name="invoicing_name"
                control={control}
                rules={{ required: true }}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <Input
                        id="invoicing_name"
                        name="invoicing_name"
                        type="text"
                        value={value}
                        ariaInvalid={formErrors['invoicing_name'] !== undefined}
                        onChange={(e) =>
                          updateField(
                            'invoicing_name',
                            e.target.value,
                            onChange
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="Invoicing address"
              errorMessage="Please provide a valid address"
              hasError={formErrors['invoice_address'] !== undefined}
            >
              <Controller
                id="invoice_address"
                name="invoice_address"
                control={control}
                rules={{
                  validate: (address) =>
                    addressValidationHandler(true, address),
                }}
                render={({ value, onChange }) => (
                  <AddressInput
                    value={value}
                    onChange={(address) =>
                      updateField('invoice_address', address, onChange)
                    }
                  />
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="Invoicing e-mail"
              errorMessage="Please provide a valid e-mail"
              hasError={formErrors['invoicing_email'] !== undefined}
            >
              <Controller
                id="invoicing_email"
                name="invoicing_email"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: MAIL_REGEX, message: 'test' },
                }}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <Input
                        id="invoicing_email"
                        name="invoicing_email"
                        type="email"
                        value={value}
                        ariaInvalid={
                          formErrors['invoicing_email'] !== undefined
                        }
                        onChange={(e) =>
                          updateField(
                            'invoicing_email',
                            e.target.value,
                            onChange
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="VAT"
              errorMessage={
                formErrors['vat']?.message || 'Please provide a valid VAT'
              }
              hasError={formErrors['vat'] !== undefined}
            >
              <Controller
                id="vat"
                name="vat"
                control={control}
                rules={{
                  validate: (value) => {
                    return (
                      value === '' ||
                      value === undefined ||
                      value === null ||
                      VAT_NUMBER_REGEX.test(value) ||
                      'Please fill in a valid VAT number'
                    );
                  },
                }}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <VatInput
                        id="vat"
                        name="vat"
                        type="text"
                        // since value can be returned as null from the API. the workaround is to show an empty string instead
                        value={value || ''}
                        ariaInvalid={formErrors['vat'] !== undefined}
                        onChange={(e) =>
                          updateField('vat', e.target.value, onChange)
                        }
                        onBlur={(e) => vatBlurHandler(e, onChange)}
                      />
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="Legal form"
              errorMessage="This field is required"
              hasError={formErrors['legal_form'] !== undefined}
            >
              <Controller
                id="legal_form"
                name="legal_form"
                control={control}
                rules={{ required: true }}
                render={({ value, onChange }) => (
                  <div tw="grow">
                    <ApiDropdown
                      testId="legalForm"
                      type="customer_legal_form"
                      mode="key"
                      onChange={(value) => {
                        if (!value) return;
                        const key =
                          typeof value === 'string'
                            ? value
                            : value.key.toString();
                        updateField('legal_form', key, onChange);
                      }}
                      value={value}
                    />
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="PO number"
              errorMessage="Please enter a number"
              hasError={formErrors['default_po_number'] !== undefined}
            >
              <Controller
                id="default_po_number"
                name="default_po_number"
                control={control}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <Input
                        id="default_po_number"
                        name="default_po_number"
                        type="text"
                        // since value can be returned as null from the API. the workaround is to show an empty string instead
                        value={value || ''}
                        ariaInvalid={
                          formErrors['default_po_number'] !== undefined
                        }
                        onChange={(e) =>
                          updateField(
                            'default_po_number',
                            e.target.value,
                            onChange
                          )
                        }
                      />
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
            <hr />
            <SettingsInput
              label="Two-Factor Authentication"
              tooltip={
                <InformationToolTip
                  name="two_factor_enabled"
                  text="When enabled, users will be required to enter a code sent to their email to log in. This check is done once a month. This setting will be enforced for all users in your company."
                  icon="information"
                />
              }
              errorMessage="Something went wrong. Please try again."
              hasError={formErrors['two_factor_enabled'] !== undefined}
            >
              <Controller
                id="two_factor_enabled"
                name="two_factor_enabled"
                control={control}
                render={({ value, onChange }) => (
                  <div tw="flex flex-row w-fit gap-4">
                    <p tw="text-gray-700 text-sm">Off</p>
                    <ToggleButton
                      id="two_factor_enabled"
                      name="two_factor_enabled"
                      value={value}
                      onChange={(e) => {
                        // On the settings screen it asks first: it changes
                        // how everyone at the customer logs in.
                        if (storesItself) {
                          setAskTwoFactor(e.target.checked);
                          return;
                        }
                        updateField(
                          'two_factor_enabled',
                          e.target.checked,
                          onChange
                        );
                      }}
                    />
                    <p tw="text-gray-700 text-sm">On</p>
                  </div>
                )}
              />
            </SettingsInput>

            <SettingsInput
              label="Candidate data deletion period (months)"
              errorMessage="Please enter a valid number between 12 and 96."
              hasError={formErrors['gdpr_deletion_period'] !== undefined}
              tooltip={
                <InformationToolTip
                  tw="-ml-4"
                  icon="information"
                  name="GDPR deletion period"
                  text="The sympl software automatically removes data related to a specific candidate after a specified amount months. This feature helps to respect candidates' privacy. Months are counted starting from the date of the most recent application by a specific candidates."
                />
              }
            >
              <Controller
                id="gdpr_deletion_period"
                name="gdpr_deletion_period"
                control={control}
                rules={{
                  min: 12,
                  max: 96,
                  valueAsNumber: true,
                  validate: (value) => {
                    return (
                      (value && value >= 12 && value <= 96) ||
                      'Please enter a number between 12 and 96'
                    );
                  },
                }}
                render={({ value, onChange }) => (
                  <div tw="flex flex-col w-full">
                    <div tw="grow">
                      <Input
                        id="gdpr_deletion_period"
                        name="gdpr_deletion_period"
                        type="number"
                        min={12}
                        max={96}
                        value={value}
                        ariaInvalid={
                          formErrors['gdpr_deletion_period'] !== undefined
                        }
                        onChange={(e) =>
                          updateField(
                            'gdpr_deletion_period',
                            e.target.value,
                            onChange
                          )
                        }
                      />
                      <Footnote mt={1}>Sympl advises 24 months.</Footnote>
                    </div>
                  </div>
                )}
              />
            </SettingsInput>
          </fieldset>

          <ContainerAction>
            {minimal && (
              <PermissionWrapper permission={PERMISSIONS.MANAGE_FINANCES}>
                <SaveButton
                  type="submit"
                  loading={updateCustomerLoading}
                  error={hasFormErrors || updateCustomerError}
                  shouldSave={!minimal && shouldSaveChanges && !hasFormErrors}
                  labelSave={saveLabel}
                />
              </PermissionWrapper>
            )}
            {locked && (
              <p tw="text-sm text-gray-500" data-testid="no-finance-rights">
                {NO_FINANCE_RIGHTS}
              </p>
            )}
            {lockedStep && (
              <p
                tw="text-sm text-gray-500 mr-auto"
                data-testid="invoicing-not-complete"
              >
                {INVOICING_NOT_COMPLETE}
              </p>
            )}
          </ContainerAction>
        </form>

        <OutOfDateBox
          refusal={refusal}
          onLoadTheirs={() => {
            abandon();
            window.setTimeout(() => window.location.reload(), 100);
          }}
          onKeepMine={() => {
            version.keepMine();
            retryRefused();
          }}
        />

        <Modal
          show={storesItself && askShorter && shorterWaits}
          onClose={() => undefined}
          hideCloseButton
        >
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="shorten-question"
          >
            <p tw="text-sm text-gray-800">{shortenQuestion(monthsNow ?? 0)}</p>
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="shorten-cancel"
                onClick={() => {
                  // Back to the period that is stored.
                  setValue(
                    'gdpr_deletion_period',
                    (monthsStored ?? 24) as never
                  );
                  setAskShorter(false);
                }}
              >
                Cancel
              </Button>
              <Button
                testId="shorten-confirm"
                onClick={() => {
                  setShorterAgreed(monthsNow);
                  setAskShorter(false);
                }}
              >
                Shorten
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          show={storesItself && askTwoFactor !== null}
          onClose={() => setAskTwoFactor(null)}
        >
          <div
            tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
            data-testid="two-factor-question"
          >
            <p tw="text-sm text-gray-800">
              {twoFactorQuestion(askTwoFactor === true)}
            </p>
            <div tw="flex gap-3 justify-end">
              <Button
                variant="secondary"
                testId="two-factor-cancel"
                onClick={() => setAskTwoFactor(null)}
              >
                Cancel
              </Button>
              <Button
                testId="two-factor-confirm"
                onClick={() => {
                  setValue('two_factor_enabled', askTwoFactor === true);
                  setAskTwoFactor(null);
                }}
              >
                {askTwoFactor ? 'Switch on' : 'Switch off'}
              </Button>
            </div>
          </div>
        </Modal>
      </Container>
    </div>
  );
};

export default GeneralSettings;
